import { idb } from "../db/index.ts";
import g from "./g.ts";
import type { Achievement, Player } from "../../../common/types.ts";
import defaultGameAttributes from "../../../common/defaultGameAttributes.ts";

const goldenOldiesCutoff = 30;
const youngGunsCutoff = 25;
const superTeamCutoff = 3;
const brickWallCutoff = 3;

const checkDynasty = async (titles: number, years: number) => {
	const teamSeasons = await idb.getCopies.teamSeasons(
		{
			tid: g.get("userTid"),
			seasons: [g.get("season") - (years - 1), Infinity],
		},
		"noCopyCache",
	);

	let titlesFound = 0;
	for (let i = 0; i < years; i++) {
		const ts = teamSeasons[teamSeasons.length - 1 - i];

		if (!ts) {
			break;
		}

		if (
			ts.playoffRoundsWon === g.get("numGamesPlayoffSeries", ts.season).length
		) {
			titlesFound += 1;
		}
	}

	return titlesFound >= titles;
};

const checkMoneyball = async (maxPayroll: number) => {
	const t = await idb.getCopy.teamsPlus(
		{
			seasonAttrs: ["expenses", "playoffRoundsWon"],
			season: g.get("season"),
			tid: g.get("userTid"),
		},
		"noCopyCache",
	);
	return !!(
		t &&
		t.seasonAttrs.playoffRoundsWon ===
			g.get("numGamesPlayoffSeries", "current").length &&
		t.seasonAttrs.expenses.salary <= maxPayroll &&
		t.seasonAttrs.expenses.salary > 0
	);
};

const checkExpansion = async (numSeasons: number) => {
	const t = await idb.getCopy.teamsPlus(
		{
			attrs: ["firstSeasonAfterExpansion"],
			seasonAttrs: ["playoffRoundsWon"],
			season: g.get("season"),
			tid: g.get("userTid"),
		},
		"noCopyCache",
	);
	return !!(
		t &&
		t.seasonAttrs.playoffRoundsWon ===
			g.get("numGamesPlayoffSeries", "current").length &&
		t.firstSeasonAfterExpansion !== undefined &&
		g.get("season") - t.firstSeasonAfterExpansion + 1 <= numSeasons
	);
};

const checkSmallMarket = async (popCutoff: number) => {
	const t = await idb.getCopy.teamsPlus(
		{
			seasonAttrs: ["playoffRoundsWon", "pop"],
			season: g.get("season"),
			tid: g.get("userTid"),
		},
		"noCopyCache",
	);
	return !!(
		t &&
		t.seasonAttrs.playoffRoundsWon ===
			g.get("numGamesPlayoffSeries", "current").length &&
		t.seasonAttrs.pop <= popCutoff
	);
};

const userWonTitle = async (season = g.get("season")) => {
	const t = await idb.getCopy.teamsPlus(
		{
			seasonAttrs: ["playoffRoundsWon"],
			season,
			tid: g.get("userTid"),
		},
		"noCopyCache",
	);
	return t
		? t.seasonAttrs.playoffRoundsWon ===
				g.get("numGamesPlayoffSeries", season).length
		: false;
};

const checkGoldenOldies = async (age: number) => {
	const wonTitle = await userWonTitle();
	if (!wonTitle) {
		return false;
	}

	const players = await idb.cache.players.indexGetAll(
		"playersByTid",
		g.get("userTid"),
	);

	return players.every((p) => g.get("season") - p.born.year >= age);
};

const checkYoungGuns = async (age: number) => {
	const wonTitle = await userWonTitle();
	if (!wonTitle) {
		return false;
	}

	const players = await idb.cache.players.indexGetAll(
		"playersByTid",
		g.get("userTid"),
	);

	return players.every((p) => g.get("season") - p.born.year <= age);
};

const checkFoFoFo = async () => {
	const playoffSeries = await idb.cache.playoffSeries.get(g.get("season"));

	if (!playoffSeries) {
		return false;
	}

	let numRoundsFound = 0;

	for (const round of playoffSeries.series) {
		let found = false;

		for (const series of round) {
			if (!series.away && series.home.tid === g.get("userTid")) {
				found = true;
				break;
			}

			if (
				series.away &&
				series.away.won >= 4 &&
				series.home.won === 0 &&
				series.away.tid === g.get("userTid")
			) {
				found = true;
				numRoundsFound += 1;
				break;
			}

			if (
				series.away &&
				series.home.won >= 4 &&
				series.away.won === 0 &&
				series.home.tid === g.get("userTid")
			) {
				found = true;
				numRoundsFound += 1;
				break;
			}
		}

		if (!found) {
			return false;
		}
	}

	return numRoundsFound >= 3;
};

const checkBrickWall = async (cutoff: number) => {
	let count = 0;
	const awards = await idb.cache.awards.get(g.get("season"));

	if (awards?.allDefensive?.[0]) {
		for (const p of awards.allDefensive[0].players) {
			if (p.tid === g.get("userTid")) {
				count += 1;
			}
		}
	}

	return count >= cutoff;
};

const checkAroundTheWorld = async (target: number) => {
	const tidsWon = new Set();
	const currentSeason = g.get("season");

	for (
		let season = g.get("startingSeason");
		season <= currentSeason;
		season++
	) {
		const tid = g.get("userTid", season);
		if (tid >= 0) {
			const teamSeason = await idb.getCopy.teamSeasons(
				{
					tid,
					season,
				},
				"noCopyCache",
			);
			if (
				teamSeason?.playoffRoundsWon ===
				g.get("numGamesPlayoffSeries", season).length
			) {
				tidsWon.add(tid);
			}
		}

		if (tidsWon.size > target) {
			return false;
		}

		if (season === currentSeason - 1 && tidsWon.size !== target - 1) {
			return false;
		}
	}

	return tidsWon.size === target;
};

const getUserSeed = async () => {
	const playoffSeries = await idb.getCopy.playoffSeries(
		{
			season: g.get("season"),
		},
		"noCopyCache",
	);

	if (!playoffSeries || playoffSeries.series.length === 0) {
		return;
	}

	for (const matchup of playoffSeries.series[0]!) {
		if (matchup.away && matchup.away.tid === g.get("userTid")) {
			return matchup.away.seed;
		}

		if (matchup.home.tid === g.get("userTid")) {
			return matchup.home.seed;
		}
	}
};

const checkSevenGameFinals = async () => {
	const playoffSeries = await idb.getCopy.playoffSeries(
		{
			season: g.get("season"),
		},
		"noCopyCache",
	);

	const matchup = playoffSeries?.series.at(-1)?.[0];

	if (matchup === undefined || matchup.away === undefined) {
		return false;
	}

	return matchup.home.won >= 3 && matchup.away.won >= 3;
};

const checkSleeperPick = async (checkPlayer: (p: Player) => boolean) => {
	const awards = await idb.cache.awards.get(g.get("season"));
	if (!awards) {
		return false;
	}

	if (awards.roy?.tid === g.get("userTid")) {
		const p = await idb.cache.players.get(awards.roy.pid);

		return !!(
			p &&
			p.tid === g.get("userTid") &&
			p.draft.tid === g.get("userTid") &&
			p.draft.year === g.get("season") - 1 &&
			checkPlayer(p)
		);
	}

	return false;
};

// IF YOU ADD TO THIS you also need to add to the whitelist in add_achievements.php
const achievements: Achievement[] = [
	{
		slug: "participation",
		name: "Participation",
		desc: "You get an achievement just for opening the game. You special snowflake!",
		category: "Meta",
	},
	{
		slug: "dynasty",
		name: "Dynasty",
		desc: "Win 6 championships in 8 years.",
		category: "Multiple Seasons",
		check() {
			return checkDynasty(6, 8);
		},
		when: "afterPlayoffs",
	},
	{
		slug: "dynasty_2",
		name: "Dynasty 2",
		desc: "Win 8 championships in a row.",
		category: "Multiple Seasons",
		check() {
			return checkDynasty(8, 8);
		},
		when: "afterPlayoffs",
	},
	{
		slug: "dynasty_3",
		name: "Dynasty 3",
		desc: "Win 11 championships in 13 years.",
		category: "Multiple Seasons",
		check() {
			return checkDynasty(11, 13);
		},
		when: "afterPlayoffs",
	},
	{
		slug: "around_the_world",
		name: "Around the World",
		desc: "Win a championship with 2 franchises in a single league.",
		category: "Multiple Seasons",
		check() {
			return checkAroundTheWorld(2);
		},
		when: "afterPlayoffs",
	},
	{
		slug: "moneyball",
		name: "Moneyball",
		desc: "Win a title with a payroll (meaning actual money paid, not your final roster's payroll) under 2/3 of the salary cap.",
		category: "Season",
		check() {
			return checkMoneyball((2 / 3) * g.get("salaryCap"));
		},
		when: "afterPlayoffs",
	},
	{
		slug: "expansion",
		name: "Expansion to Champion",
		desc: "Win a title as an expansion team within its first 5 seasons.",
		category: "Season",
		check() {
			return checkExpansion(5);
		},
		when: "afterPlayoffs",
	},
	{
		slug: "expansion_2",
		name: "Expansion to Champion 2",
		desc: "Win a title as an expansion team within its first 3 seasons.",
		category: "Season",
		check() {
			return checkExpansion(3);
		},
		when: "afterPlayoffs",
	},
	{
		slug: "expansion_3",
		name: "Expansion to Champion 3",
		desc: "Win a title as an expansion team in its first season.",
		category: "Season",
		check() {
			return checkExpansion(1);
		},
		when: "afterPlayoffs",
	},
	{
		slug: "small_market",
		name: "Small Market",
		desc: "Win a title in a city with a population under 2 million people.",
		category: "Season",
		check() {
			return checkSmallMarket(2);
		},
		when: "afterPlayoffs",
	},
	{
		slug: "septuawinarian",
		name: "Septuawinarian",
		desc: "Win 70+ games in the regular season.",
		category: "Season",
		async check() {
			const t = await idb.getCopy.teamsPlus(
				{
					seasonAttrs: ["won"],
					season: g.get("season"),
					tid: g.get("userTid"),
				},
				"noCopyCache",
			);
			return !!(
				t &&
				t.seasonAttrs.won / g.get("numGames") >=
					70 / defaultGameAttributes.numGames[0].value
			);
		},
		when: "afterRegularSeason",
	},
	{
		slug: "98_degrees",
		name: "98 Degrees",
		desc: "Go 98-0 in the playoffs and regular season.",
		category: "Season",
		async check() {
			const playoffSeries = await idb.cache.playoffSeries.get(g.get("season"));

			if (!playoffSeries) {
				return false;
			}

			let playoffWins = 0;

			for (const round of playoffSeries.series) {
				for (const series of round) {
					if (series.away && series.away.tid === g.get("userTid")) {
						if (series.home.won > 0) {
							return false;
						}
						playoffWins += series.away.won;
					}

					if (series.away && series.home.tid === g.get("userTid")) {
						if (series.away.won > 0) {
							return false;
						}
						playoffWins += series.home.won;
					}
				}
			}

			if (playoffWins < 16) {
				return false;
			}

			const t = await idb.getCopy.teamsPlus(
				{
					seasonAttrs: ["won", "lost", "tied", "otl"],
					season: g.get("season"),
					tid: g.get("userTid"),
				},
				"noCopyCache",
			);

			return !!(
				t &&
				t.seasonAttrs.won >= 82 &&
				t.seasonAttrs.lost === 0 &&
				t.seasonAttrs.tied === 0 &&
				t.seasonAttrs.otl === 0
			);
		},
		when: "afterPlayoffs",
	},
	{
		slug: "homegrown",
		name: "Homegrown",
		desc: "Win a title with only players your team drafted.",
		category: "Team Composition",
		async check() {
			const wonTitle = await userWonTitle();
			if (!wonTitle) {
				return false;
			}

			const players = await idb.cache.players.indexGetAll(
				"playersByTid",
				g.get("userTid"),
			);

			return players.every((p) => p.draft.tid === g.get("userTid"));
		},
		when: "afterPlayoffs",
	},
	{
		slug: "golden_oldies",
		name: "Golden Oldies",
		desc: `Win a title when your entire team is at least ${goldenOldiesCutoff} years old.`,
		category: "Team Composition",
		check() {
			return checkGoldenOldies(goldenOldiesCutoff);
		},
		when: "afterPlayoffs",
	},
	{
		slug: "young_guns",
		name: "Young Guns",
		desc: `Win a title when your entire team is at most ${youngGunsCutoff} years old.`,
		category: "Team Composition",
		check() {
			return checkYoungGuns(youngGunsCutoff);
		},
		when: "afterPlayoffs",
	},
	{
		slug: "one_draft_class",
		name: "One Draft Class",
		desc: "Win a title when your entire roster comes from the same draft class.",
		category: "Team Composition",
		async check() {
			const wonTitle = await userWonTitle();
			if (!wonTitle) {
				return false;
			}

			const players = await idb.cache.players.indexGetAll(
				"playersByTid",
				g.get("userTid"),
			);

			if (players[0]) {
				const year = players[0].draft.year;
				return players.every((p) => p.draft.year === year);
			}

			return false;
		},
		when: "afterPlayoffs",
	},
	{
		slug: "small_ball",
		name: "Small Ball",
		desc: "Win a championship without any players over 6'5\".",
		category: "Team Composition",
		async check() {
			const wonTitle = await userWonTitle();
			if (!wonTitle) {
				return false;
			}

			const players = await idb.cache.players.indexGetAll(
				"playersByTid",
				g.get("userTid"),
			);

			const cutoff = 77; // 6'5" in inches
			return players.every((p) => p.hgt <= cutoff);
		},
		when: "afterPlayoffs",
	},
	{
		slug: "super_team",
		name: "Super Team",
		desc: `Have ${superTeamCutoff}+ players on the All-League First Team.`,
		category: "Awards",
		async check() {
			let count = 0;
			const awards = await idb.cache.awards.get(g.get("season"));

			if (awards?.allLeague?.[0]) {
				for (const p of awards.allLeague[0].players) {
					if (p.tid === g.get("userTid")) {
						count += 1;
					}
				}
			}

			return count >= superTeamCutoff;
		},
		when: "afterAwards",
	},
	{
		slug: "golden_boy",
		name: "Golden Boy",
		desc: "Have a rookie make an All-League Team.",
		category: "Awards",
		async check() {
			const awards = await idb.cache.awards.get(g.get("season"));

			if (awards?.allLeague) {
				for (const team of awards.allLeague) {
					for (const { pid, tid } of team.players) {
						if (tid === g.get("userTid")) {
							const p = await idb.cache.players.get(pid);

							if (p && p.draft.year === g.get("season") - 1) {
								return true;
							}
						}
					}
				}
			}

			return false;
		},
		when: "afterAwards",
	},
	{
		slug: "triple_crown",
		name: "Triple Crown",
		desc: "Have a player win MVP, Finals MVP, and DPOY in the same year.",
		category: "Awards",
		async check() {
			const awards = await idb.cache.awards.get(g.get("season"));

			return !!(
				awards?.mvp &&
				awards.finalsMvp &&
				awards.dpoy &&
				awards.mvp.tid === g.get("userTid") &&
				awards.finalsMvp.tid === g.get("userTid") &&
				awards.dpoy.tid === g.get("userTid") &&
				awards.mvp.pid === awards.finalsMvp.pid &&
				awards.mvp.pid === awards.dpoy.pid
			);
		},
		when: "afterAwards",
	},
	{
		slug: "brick_wall",
		name: "Brick Wall",
		desc: "Have 3+ players on the All-Defensive First Team.",
		category: "Awards",
		check() {
			return checkBrickWall(brickWallCutoff);
		},
		when: "afterAwards",
	},
	{
		slug: "out_of_nowhere",
		name: "Out Of Nowhere",
		desc: "Have a player win both MIP and MVP in the same year.",
		category: "Awards",
		async check() {
			const awards = await idb.cache.awards.get(g.get("season"));
			return !!(
				awards?.mvp &&
				awards.mip &&
				awards.mvp.tid === g.get("userTid") &&
				awards.mip.tid === g.get("userTid") &&
				awards.mvp.pid === awards.mip.pid
			);
		},
		when: "afterAwards",
	},
	{
		slug: "sleeper_pick",
		name: "Sleeper Pick",
		desc: "Use a non-lottery pick to draft the ROY.",
		category: "Draft",
		check() {
			return checkSleeperPick((p) => p.draft.round > 1 || p.draft.pick >= 15);
		},
		when: "afterAwards",
	},
	{
		slug: "clutch_finish",
		name: "Clutch Finish",
		desc: "Win game 7 of the finals in OT.",
		category: "Playoffs",
		async check() {
			const sevenGameFinals = await checkSevenGameFinals();
			if (!sevenGameFinals) {
				return false;
			}

			const games = await idb.cache.games.getAll();
			const game = games.at(-1);

			return !!game && game.overtimes >= 1 && game.won.tid === g.get("userTid");
		},
		when: "afterPlayoffs",
	},
	{
		slug: "fo_fo_fo",
		name: "Fo Fo Fo",
		desc: "Go undefeated in the playoffs.",
		category: "Playoffs",
		check: checkFoFoFo,
		when: "afterPlayoffs",
	},
	{
		slug: "fo_fo_fo_2",
		name: "Fo Fo Fo 2",
		desc: "Go undefeated in the playoffs, without the #1 seed.",
		category: "Playoffs",
		async check() {
			const foFoFo = await checkFoFoFo();
			if (!foFoFo) {
				return false;
			}

			const seed = await getUserSeed();
			return seed !== undefined && seed > 1;
		},
		when: "afterPlayoffs",
	},
	{
		slug: "living_dangerously",
		name: "Living Dangerously",
		desc: "Win every playoff series 4-3.",
		category: "Playoffs",
		async check() {
			const playoffSeries = await idb.cache.playoffSeries.get(g.get("season"));

			if (!playoffSeries) {
				return false;
			}

			let numRoundsFound = 0;

			for (const round of playoffSeries.series) {
				let found = false;

				for (const series of round) {
					if (!series.away && series.home.tid === g.get("userTid")) {
						found = true;
						break;
					}

					if (
						series.away &&
						series.away.won >= 4 &&
						series.home.won === series.away.won - 1 &&
						series.away.tid === g.get("userTid")
					) {
						found = true;
						numRoundsFound += 1;
						break;
					}

					if (
						series.away &&
						series.home.won >= 4 &&
						series.away.won === series.home.won - 1 &&
						series.home.tid === g.get("userTid")
					) {
						found = true;
						numRoundsFound += 1;
						break;
					}
				}

				if (!found) {
					return false;
				}
			}

			return numRoundsFound >= 3;
		},
		when: "afterPlayoffs",
	},
	{
		slug: "underdog",
		name: "Underdog",
		desc: "Win a title as the 8th seed.",
		category: "Playoffs",
		async check() {
			const wonTitle = await userWonTitle();
			if (!wonTitle) {
				return false;
			}

			const seed = await getUserSeed();
			return seed === 8;
		},
		when: "afterPlayoffs",
	},
	{
		slug: "finals_choke",
		name: "Finals Choke",
		desc: "Blow a 3-0 lead in the finals.",
		category: "Playoffs",
		async check() {
			const t = await idb.getCopy.teamsPlus(
				{
					seasonAttrs: ["playoffRoundsWon"],
					season: g.get("season"),
					tid: g.get("userTid"),
				},
				"noCopyCache",
			);

			if (
				!t ||
				t.seasonAttrs.playoffRoundsWon !==
					g.get("numGamesPlayoffSeries", "current").length - 1
			) {
				return false;
			}

			const sevenGameFinals = await checkSevenGameFinals();
			if (!sevenGameFinals) {
				return false;
			}

			const games = await idb.cache.games.getAll();
			const last4 = games.slice(-4);

			return last4.every((game) => game.lost.tid === g.get("userTid"));
		},
		when: "afterPlayoffs",
	},
	{
		slug: "first_round_choke",
		name: "First Round Choke",
		desc: "Lose in the first round of the playoffs as the #1 seed.",
		category: "Playoffs",
		async check() {
			const t = await idb.getCopy.teamsPlus(
				{
					seasonAttrs: ["playoffRoundsWon"],
					season: g.get("season"),
					tid: g.get("userTid"),
				},
				"noCopyCache",
			);

			if (!t || t.seasonAttrs.playoffRoundsWon !== 0) {
				return false;
			}

			const seed = await getUserSeed();
			return seed === 1;
		},
		when: "afterPlayoffs",
	},
];

export default achievements;
